import { useEffect, useRef, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, ChevronLeft, ChevronRight, PackageOpen, X, Loader2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * DataTable — reusable list w/ optional search + pagination + bulk selection.
 * Props:
 *  - columns: [{ key, header, render?, className?, testId? }]
 *  - rows: array
 *  - keyField: string (defaults "id")
 *  - loading: bool
 *  - onSearch?: fn(q) — if provided, shows search input
 *  - searchPlaceholder?: string
 *  - page?, limit?, total?, onPage?: (n) => void
 *  - onRowClick?: (row) => void
 *  - empty?: node
 *  - testId: string
 *  - selection?: { isSelected(id), toggle(id), toggleAll(ids, forceOn?), ids: string[] }
 */
export default function DataTable({
  columns, rows = [], keyField = "id", loading = false,
  onSearch, searchPlaceholder = "Search…",
  page, limit, total, onPage,
  onRowClick, empty, testId,
  selection,
}) {
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const debRef = useRef(null);
  const lastSentRef = useRef("");
  const hasPager = onPage && typeof page === "number";
  const totalPages = hasPager ? Math.max(1, Math.ceil((total || 0) / (limit || 20))) : 1;
  const pageIds = rows.map((r) => r[keyField]).filter(Boolean);
  const allOn = !!selection && pageIds.length > 0 && pageIds.every((id) => selection.isSelected(id));
  const someOn = !!selection && !allOn && pageIds.some((id) => selection.isSelected(id));

  // Sprint 56 — search-as-you-type. Debounces onSearch by 250ms so users
  // never need to press Enter. Also exposes a clear (X) affordance.
  const handleQChange = (val) => {
    setQ(val);
    setSearching(true);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => {
      if (lastSentRef.current !== val) {
        lastSentRef.current = val;
        onSearch?.(val);
      }
      setSearching(false);
    }, 250);
  };
  useEffect(() => () => { if (debRef.current) clearTimeout(debRef.current); }, []);

  const clearSearch = () => {
    if (debRef.current) clearTimeout(debRef.current);
    setQ("");
    lastSentRef.current = "";
    setSearching(false);
    onSearch?.("");
  };

  return (
    <div className="card-soft overflow-hidden" data-testid={testId}>
      {onSearch && (
        <div className="p-3 border-b bg-secondary/40 sticky top-0 z-10 backdrop-blur">
          <form
            className="relative max-w-md"
            onSubmit={(e) => { e.preventDefault(); if (debRef.current) clearTimeout(debRef.current); lastSentRef.current = q; onSearch(q); setSearching(false); }}
          >
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => handleQChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9 pr-16 bg-card h-10"
              data-testid={`${testId}-search`}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {searching && q && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />}
              {q && !searching && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="text-muted-foreground hover:text-foreground p-1 rounded"
                  data-testid={`${testId}-search-clear`}
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-secondary/30">
            <TableRow>
              {selection && (
                <TableHead className="w-10 pl-4 pr-0">
                  <Checkbox
                    checked={allOn ? true : someOn ? "indeterminate" : false}
                    onCheckedChange={() => selection.toggleAll(pageIds)}
                    aria-label="Select all"
                    data-testid={`${testId}-select-all`}
                  />
                </TableHead>
              )}
              {columns.map((c) => (
                <TableHead key={c.key} className={c.className}>{c.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {selection && <TableCell className="w-10 pl-4"><Skeleton className="h-4 w-4" /></TableCell>}
                {columns.map((c) => (
                  <TableCell key={c.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}

            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (selection ? 1 : 0)}>
                  <div className="py-14 flex flex-col items-center gap-2 text-muted-foreground">
                    <PackageOpen className="w-8 h-8 opacity-60" />
                    {empty || "Nothing here yet."}
                  </div>
                </TableCell>
              </TableRow>
            )}

            {rows.map((r, idx) => {
              const rowId = r[keyField];
              const isSel = !!selection && selection.isSelected(rowId);
              return (
                <TableRow
                  key={rowId || idx}
                  onClick={onRowClick ? () => onRowClick(r) : undefined}
                  className={`table-row-hover ${onRowClick ? "cursor-pointer" : ""} ${isSel ? "bg-primary/5" : ""}`}
                  data-testid={`${testId}-row-${idx}`}
                  data-selected={isSel || undefined}
                >
                  {selection && (
                    <TableCell
                      className="w-10 pl-4 pr-0"
                      onClick={(e) => { e.stopPropagation(); selection.toggle(rowId); }}
                    >
                      <Checkbox
                        checked={isSel}
                        onCheckedChange={() => selection.toggle(rowId)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Select row"
                        data-testid={`${testId}-select-${idx}`}
                      />
                    </TableCell>
                  )}
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.className} data-testid={c.testId?.(r)}>
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {hasPager && (
        <div className="flex items-center justify-between px-4 py-3 border-t bg-secondary/20 text-sm">
          <div className="text-muted-foreground">
            {total ? (
              <>Showing <span className="font-semibold text-foreground">{(page - 1) * limit + 1}</span>–
                <span className="font-semibold text-foreground">{Math.min(page * limit, total)}</span> of
                {" "}<span className="font-semibold text-foreground">{total}</span></>
            ) : "—"}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testId}-prev`}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="text-xs text-muted-foreground">Page {page} / {totalPages}</div>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPage(page + 1)} data-testid={`${testId}-next`}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
